import { useCallback, useState } from 'react'
import { useCodeContext } from '~/hooks/useCodeContext'
import {
  AgentRequest,
  CodeContext,
  Command,
  CommandType,
  ContextUpload,
  FileUpload,
  IntegrationPlatform,
  Metadata,
  MigrationDocumentInput,
  integrationPlatformToJSON
} from '~/protos/agents'
import { parseRepositoryName } from '~/utils/repositoryUtils'
import { BetterToast } from '~/utils/toast'
import { TaskParams } from './BaseTaskForm'
import { MuleRefactorRepoSelectionScreen } from './shared/mule-refactor/MuleRefactorRepoSelectionScreen'

interface MuleRefactorContainerProps {
  onClose: () => void
  onTaskSubmit: (taskParams: TaskParams) => Promise<boolean>
}

interface RepoSavePayload {
  legacyRepos: { conn_type: string; repository: string; branch: string }[]
  legacyOption: 'codeContext' | 'upload' | string
  legacyS3Paths: string[]
  mulesoftRepos: { conn_type: string; repository: string; branch: string }[]
  mulesoftOption?: 'codeContext' | 'upload' | string
  mulesoftS3Paths: string[]
  platform: IntegrationPlatform
  metadataS3Paths?: string[]
}

function buildCodeContexts(
  option: string | undefined,
  repos: { conn_type: string; repository: string; branch: string }[],
  s3Paths: string[]
): CodeContext[] {
  if (option === 'codeContext') {
    return repos.map(repo => {
      const [orgName, repoName] = parseRepositoryName(repo.repository)
      return CodeContext.create({
        connType: repo.conn_type,
        orgName,
        repoName,
        branchName: repo.branch
      })
    })
  }
  if (option === 'upload') {
    return s3Paths.map(s3TarFilePath => CodeContext.create({ connType: 'S3', s3TarFilePath }))
  }
  return []
}

function buildContextUpload(metadataS3Paths?: string[]): ContextUpload | undefined {
  if (!metadataS3Paths || metadataS3Paths.length === 0) return undefined
  return ContextUpload.create({
    files: metadataS3Paths.map(s3Path =>
      FileUpload.create({
        fileName: s3Path.split('/').pop() || 'file',
        fileS3Path: s3Path
      })
    )
  })
}

export function MuleRefactorContainer({ onClose, onTaskSubmit }: MuleRefactorContainerProps) {
  const { repositories } = useCodeContext()
  // Locks the "Next: Select Endpoints" button while the task is being created.
  // On success the parent swaps this screen for the in-task flow, so we never
  // re-enable; on failure we release it so the user can retry.
  const [isSubmitting, setIsSubmitting] = useState(false)

  const handleRepoSave = useCallback(
    async (payload: RepoSavePayload) => {
      const multiRepoCodeContext = buildCodeContexts(
        payload.legacyOption,
        payload.legacyRepos,
        payload.legacyS3Paths
      )
      const auxiliaryCodeContext = buildCodeContexts(
        payload.mulesoftOption,
        payload.mulesoftRepos,
        payload.mulesoftS3Paths
      )

      const agentRequest = AgentRequest.create({
        command: Command.create({
          commandType: CommandType.MULE_REFACTORING,
          commandString: `Platform: ${integrationPlatformToJSON(payload.platform)}`,
          contextUpload: buildContextUpload(payload.metadataS3Paths)
        }),
        metadata: Metadata.create({
          codeContext: multiRepoCodeContext.length === 1 ? multiRepoCodeContext[0] : undefined,
          multiRepoCodeContext,
          auxiliaryCodeContext: auxiliaryCodeContext.length > 0 ? auxiliaryCodeContext : undefined
        }),
        migrationDocInput: MigrationDocumentInput.create({ platform: payload.platform })
      })

      setIsSubmitting(true)
      try {
        const isSuccess = await onTaskSubmit({ request: agentRequest })
        if (!isSuccess) {
          BetterToast.error('Failed to start refactoring task')
          setIsSubmitting(false)
        }
        // On success, leave the button locked: the parent transitions away from
        // this screen once the task exists, so re-enabling would only flicker.
      } catch (error) {
        console.error('Failed to submit Mule Refactor task:', error)
        BetterToast.error('Failed to start refactoring task')
        setIsSubmitting(false)
      }
    },
    [onTaskSubmit]
  )

  return (
    <MuleRefactorRepoSelectionScreen
      onClose={onClose}
      repositories={repositories}
      onSave={handleRepoSave}
      isSubmitting={isSubmitting}
    />
  )
}
